import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Sparkles, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Footer from './components/Footer';
import './App.css';
import VideoDisplay from '../src/components/VideoDisplay';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import GoogleSignIn from './components/GoogleSignIn';
import UserMenu from './components/UserMenu';
import SubscriptionPrompt from './components/SubscriptionPrompt';
import CustomDropdown from './components/CustomDropdown';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import axios from 'axios';
import API_URL from './config';

const MainApp = () => {
  const { loading, isAuthenticated, refreshUser } = useAuth();
  const [isAIToggle, setIsAIToggle] = useState(false);
  const [userScript, setUserScript] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [topic, setTopic] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAIScriptLoading, setIsAIScriptLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Generating video...");
  const [generatedAIScript, setGeneratedAIScript] = useState("");
  const [scriptReviewMode, setScriptReviewMode] = useState(false);
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false);
  const [showLastVideoView, setShowLastVideoView] = useState(false);
  const [lastVideoUrl, setLastVideoUrl] = useState("");
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'privacy', 'terms'
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Game and aspect ratio selection
  const [selectedGame, setSelectedGame] = useState("minecraft");
  const [aspectRatio, setAspectRatio] = useState("landscape");
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [modalVideoUrl, setModalVideoUrl] = useState("");

  const gameOptions = [
    { value: 'minecraft', label: 'Minecraft' },
    { value: 'fortnite', label: 'Fortnite' },
    { value: 'gtamegaramp', label: 'GTA MegaRamp' },
    { value: 'subwaysurfers', label: 'Subway Surfers' },
  ];

  const aspectRatioOptions = [
    { value: 'landscape', label: 'Landscape (16:9)' },
    { value: 'portrait', label: 'Portrait (9:16)' },
  ];

  // Define available clips for each game/aspect combination
  const availableClips = {
    minecraft: {
      landscape: 3,
      portrait: 6
    },
    fortnite: {
      landscape: 2,
      portrait: 2
    },
    gtamegaramp: {
      landscape: 4,
      portrait: 4
    },
    subwaysurfers: {
      landscape: 2,
      portrait: 2
    }
  };

  // Get total clips for current selection
  const totalClips = availableClips[selectedGame]?.[aspectRatio] || 1;

  // Handle clip navigation
  const handlePreviousClip = () => {
    setCurrentClipIndex((prev) => (prev > 0 ? prev - 1 : totalClips - 1));
  };

  const handleNextClip = () => {
    setCurrentClipIndex((prev) => (prev < totalClips - 1 ? prev + 1 : 0));
  };

  // Reset clip index when game or aspect ratio changes
  useEffect(() => {
    setCurrentClipIndex(0);
  }, [selectedGame, aspectRatio]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePreviewModal();
      }
    };

    if (isPreviewModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPreviewModalOpen]);

  const openPreviewModal = (videoSrc) => {
    setModalVideoUrl(videoSrc);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setModalVideoUrl("");
  };

  // AI Mode: System Prompt State
  const [systemPromptStyle, setSystemPromptStyle] = useState("clickbait");
  const [customSystemPrompt, setCustomSystemPrompt] = useState("");

  // Handle toggle switch for AI script vs user script
  const handleToggleChange = () => {
    setIsAIToggle(!isAIToggle);
  };

  // Character limit for user script
  const MAX_CHARS = 1000;

  // Handle user script input (allow any length, just disable button if over)
  const handleScriptChange = (e) => {
    setUserScript(e.target.value);
  };

  // Handle topic input change
  const handleTopicChange = (e) => {
    setTopic(e.target.value);
  };

  // Handle form submission (generate video)
  const handleGenerateVideo = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      alert('Please sign in to generate videos');
      return;
    }

    if (isAIToggle) {
      // AI-Assisted Scripting: Step 1 - Generate AI Script
      if (!topic.trim()) {
        alert('Please enter a topic for the AI script!');
        return;
      }
      setLoadingMessage("Generating AI script...");
      setIsAIScriptLoading(true);
      setScriptReviewMode(false);
      try {
        // Compose system prompt for AI mode
        let systemPrompt = "";
        if (systemPromptStyle === "custom") {
          systemPrompt = customSystemPrompt || "Write a script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }";
        } else if (systemPromptStyle === "clickbait") {
          systemPrompt = "Write a viral, clickbaity YouTube Shorts script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }";
        } else if (systemPromptStyle === "educational") {
          systemPrompt = "Write an educational, informative script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }";
        } else if (systemPromptStyle === "news") {
          systemPrompt = "Write a concise news summary script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }";
        } else if (systemPromptStyle === "story") {
          systemPrompt = "Write a creative, storytelling script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }";
        }
        const response = await axios.post(`${API_URL}/api/generate-script-text/`, {
          topic: topic,
          system_prompt: systemPrompt
        });
        let scriptText = "";
        // Try direct JSON parse
        if (typeof response.data === "string") {
          try {
            // Try direct parse
            const parsed = JSON.parse(response.data);
            if (parsed && typeof parsed === "object" && parsed.script_text) {
              scriptText = parsed.script_text;
            } else {
              // Try double-parse (if it's a JSON string inside a string)
              const doubleParsed = JSON.parse(parsed);
              if (doubleParsed && typeof doubleParsed === "object" && doubleParsed.script_text) {
                scriptText = doubleParsed.script_text;
              }
            }
          } catch {
            // Try to extract JSON from embedded text
            const match = response.data.match(/\{[\s\S]*\}/);
            if (match) {
              try {
                const extracted = JSON.parse(match[0]);
                if (extracted && typeof extracted === "object" && extracted.script_text) {
                  scriptText = extracted.script_text;
                }
              } catch {
                scriptText = "";
              }
            }
          }
        } else if (typeof response.data === "object" && response.data !== null) {
          scriptText = response.data.script_text || "";
        }
        if (scriptText) {
          setGeneratedAIScript(scriptText);
          setScriptReviewMode(true);
        } else {
          alert('Failed to generate AI script. Please try again.');
        }
      } catch (error) {
        console.error("Error fetching AI script:", error);
        alert("An error occurred while generating the AI script. Please try again.");
      } finally {
        setIsAIScriptLoading(false);
      }
    } else {
      // User-Provided Script: Proceed directly to video generation
      if (!userScript.trim()) {
        alert('Please enter your script!');
        return;
      }
      await generateVideo(userScript);
    }
  };

  const generateVideo = async (script) => {
    setLoadingMessage("Queuing video generation...");
    setIsLoading(true);
    try {
      const clipName = `clip${currentClipIndex + 1}`;
      
      // Step 1: Submit job to queue
      const response = await axios.post(`${API_URL}/generate-video`, {
        text: script,
        game: selectedGame,
        aspect_ratio: aspectRatio,
        clip_name: clipName
      });
      
      if (response.data?.job_id) {
        const jobId = response.data.job_id;
        
        // Update loading message with queue position
        if (response.data.queue_position > 0) {
          setLoadingMessage(`Queued (Position ${response.data.queue_position})`);
        } else {
          setLoadingMessage("Starting video generation...");
        }
        
        // Step 2: Poll for job status and progress
        await pollJobProgress(jobId);
        
        // Refresh user data to get updated trial/subscription status
        await refreshUser();
      } else {
        alert("Failed to queue video generation. Please try again.");
      }
    } catch (error) {
      console.error("Error generating video:", error);
      if (error.response?.status === 402) {
        // Payment required - check if trial is available
        const errorDetail = error.response?.data?.detail;
        const trialAvailable = errorDetail?.trial_available || false;
        
        // Store trial availability for the subscription prompt
        window.trialAvailable = trialAvailable;
        setShowSubscriptionPrompt(true);
      } else if (error.response?.status === 401) {
        // Unauthorized
        alert("Please sign in again to continue.");
      } else {
        alert("An error occurred while generating the video. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const pollJobProgress = async (jobId) => {
    const maxAttempts = 300; // 5 minutes max (300 * 1 second)
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const statusResponse = await axios.get(`${API_URL}/video-generation/${jobId}/status`);
        const status = statusResponse.data;
        
        // Update loading message and progress percentage
        if (status.queue_position > 0) {
          setLoadingMessage(`Queued (Position ${status.queue_position})`);
          setProgressPercentage(0); // No progress while queued
        } else {
          setLoadingMessage(status.progress_message || "Processing...");
          setProgressPercentage(status.progress || 0);
        }
        
        // Check if job is complete
        if (status.status === 'completed') {
          if (status.result_data?.video_url) {
            setVideoUrl(status.result_data.video_url);
            return; // Success!
          } else {
            throw new Error("Video generation completed but no video URL received");
          }
        } else if (status.status === 'failed') {
          throw new Error(status.error_message || "Video generation failed");
        }
        
        // Wait 1 second before next poll
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
        
      } catch (error) {
        if (error.response?.status === 404) {
          throw new Error("Job not found - it may have expired");
        }
        throw error;
      }
    }
    
    // If we get here, we timed out
    throw new Error("Video generation timed out. Please try again.");
  };

  const handleProceedWithAIScript = async () => {
    if (!generatedAIScript.trim()) {
      alert('AI script is empty. Please edit or re-generate.');
      return;
    }
    setScriptReviewMode(false);
    await generateVideo(generatedAIScript);
  };

  // Reset fields to go back to the home state
  const handleGoHome = () => {
    setVideoUrl("");
    setTopic("");
    setUserScript("");
    setGeneratedAIScript("");
    setScriptReviewMode(false);
    setIsAIToggle(false);
  };

  // Expose callback for UserMenu to trigger last video view
  if (typeof window !== "undefined") {
    window.BrainRotAIUserMenuProps = {
      onViewLastVideo: (url) => {
        setLastVideoUrl(url);
        setShowLastVideoView(true);
      }
    };
  }

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-container">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="loading-spinner"
          />
          <p className="loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Modern Header */}
      <motion.header 
        className="app-header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="header-content">
          <motion.div 
            className="logo"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Sparkles className="logo-icon" />
            <span className="logo-text">BrainRotAI</span>
          </motion.div>
          
          {isAuthenticated && (
            <motion.div 
              className="user-section"
              style={{ position: 'relative' }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <UserMenu />
            </motion.div>
          )}
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="main-content">
        {/* Show subscription prompt if needed */}
        <AnimatePresence>
          {showSubscriptionPrompt && (
            <SubscriptionPrompt onClose={() => setShowSubscriptionPrompt(false)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isPreviewModalOpen && (
            <motion.div
              className="video-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePreviewModal}
            >
              <motion.div
                className="video-modal-content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <video src={modalVideoUrl} controls autoPlay className="video-modal-player" />
                <button onClick={closePreviewModal} className="video-modal-close">
                  <X size={24} />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {currentPage === 'privacy' ? (
          <PrivacyPolicy onBack={() => setCurrentPage('home')} />
        ) : currentPage === 'terms' ? (
          <TermsOfService onBack={() => setCurrentPage('home')} />
        ) : showLastVideoView ? (
          <motion.div 
            className={`result-section ${aspectRatio}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <VideoDisplay videoUrl={lastVideoUrl} />
            <motion.button
              className="back-button"
              onClick={() => {
                setShowLastVideoView(false);
                setLastVideoUrl("");
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Generate New Video
            </motion.button>
          </motion.div>
        ) : isLoading ? (
          <motion.div 
            className="loading-state"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="loading-spinner large"
            />
            <h2 className="loading-title">Creating Magic</h2>
            <p className="loading-subtitle">{loadingMessage}</p>
            
            {/* Progress Bar */}
            <div style={{ 
              width: '100%', 
              maxWidth: '400px', 
              margin: '20px auto',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              overflow: 'hidden',
              height: '8px'
            }}>
              <motion.div
                style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #7C3AED, #10B981)',
                  borderRadius: '10px',
                  width: `${progressPercentage}%`
                }}
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            
            {/* Progress Percentage */}
            <div style={{ 
              color: "#7CFFCB", 
              fontWeight: 600, 
              fontSize: "1.1em", 
              textAlign: "center",
              marginBottom: "10px"
            }}>
              {progressPercentage}%
            </div>
            
            <div style={{ marginTop: 16, color: "#10b981", fontWeight: 500, fontSize: "1em", textAlign: "center" }}>
              If you reload or navigate away, your finished video will appear under your account in the <b>'Last Video'</b> section.
            </div>
          </motion.div>
        ) : !videoUrl ? (
          <motion.div 
            className="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Hero Section */}
            <div className="hero-section">
              <motion.h1 
                className="hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                Create Viral Content
                <span className="gradient-text"> Instantly</span>
              </motion.h1>
              <motion.p 
                className="hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Transform your ideas into engaging brainrot videos with AI-powered storytelling
              </motion.p>
            </div>

            {/* Content Grid */}
            <div className="content-grid">
              {/* Video Preview */}
              <motion.div 
                className="preview-section"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                {/* Clip Navigation */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <motion.button
                    onClick={handlePreviousClip}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}
                  >
                    <ChevronLeft size={20} />
                  </motion.button>
                  
                  <span style={{ 
                    color: '#fff', 
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    Clip {currentClipIndex + 1} of {totalClips}
                  </span>
                  
                  <motion.button
                    onClick={handleNextClip}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}
                  >
                    <ChevronRight size={20} />
                  </motion.button>
                </div>

                <div className={`preview-container ${aspectRatio}`}>
                  <video
                    key={`${selectedGame}-${aspectRatio}-${currentClipIndex}`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="preview-video"
                    onError={(e) => {
                      console.error('Video failed to load:', e.target.src);
                    }}
                  >
                    {/* Use R2 Preview URLs - try .mp4 first for landscape, .mov for portrait */}
                    <source 
                      src={`https://pub-5c2e5437b89c40a0abd359eb66af869d.r2.dev/${selectedGame}/${aspectRatio}/clip${currentClipIndex + 1}${aspectRatio === 'landscape' ? '.mp4' : '.mov'}`} 
                      type="video/mp4" 
                    />
                    {/* Fallback to the other extension */}
                    <source 
                      src={`https://pub-5c2e5437b89c40a0abd359eb66af869d.r2.dev/${selectedGame}/${aspectRatio}/clip${currentClipIndex + 1}${aspectRatio === 'landscape' ? '.mov' : '.mp4'}`} 
                      type="video/mp4" 
                    />
                  </video>
                  <div className="preview-overlay" onClick={() => openPreviewModal(`https://pub-5c2e5437b89c40a0abd359eb66af869d.r2.dev/${selectedGame}/${aspectRatio}/clip${currentClipIndex + 1}${aspectRatio === 'landscape' ? '.mp4' : '.mov'}`)}>
                    <Play className="play-icon" />
                  </div>
                  <div className="preview-caption-wrapper">
                    <p className="preview-caption">Your ideas transformed into viral content</p>
                  </div>
                </div>
              </motion.div>

              {/* Input Section */}
              <motion.div 
                className="input-section"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              >
                {!isAuthenticated ? (
                  <div className="auth-card">
                    <div className="auth-content">
                      <h2>Get Started</h2>
                      <p>Sign in to start creating amazing content</p>
                      <GoogleSignIn
                        onSuccess={() => {
                          // User authenticated successfully
                        }}
                        onError={(error) => {
                          alert(`Authentication failed: ${error}`);
                        }}
                      />
                      {/* Removed duplicate trial info to avoid repetition */}
                    </div>
                  </div>
                ) : (
                  <div className="creation-panel">
                    {!scriptReviewMode ? (
                      <>
                        {/* AI Toggle */}
                        <div className="toggle-section">
                          <motion.button
                            className={`toggle-button ${isAIToggle ? 'active' : ''}`}
                            onClick={handleToggleChange}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Sparkles className="toggle-icon" />
                            <span>AI-Assisted</span>
                            <div className={`toggle-switch ${isAIToggle ? 'on' : 'off'}`}>
                              <div className="toggle-knob" />
                            </div>
                          </motion.button>
                        </div>

                        {isAIScriptLoading ? (
                          <div className="ai-loading-panel">
                            <div className="loading-spinner" style={{ margin: "2rem auto" }} />
                            <p className="loading-text" style={{ textAlign: "center" }}>Generating AI script...</p>
                            <button
                              className="secondary-button"
                              style={{ margin: "1.5rem auto 0" }}
                              onClick={() => setIsAIScriptLoading(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* System Prompt Dropdown */}
                            {isAIToggle && (
                              <div className="input-group" style={{ marginBottom: "1rem" }}>
                                <label className="input-label" htmlFor="system-prompt-style">
                                  Prompt Style
                                </label>
                                <select
                                  id="system-prompt-style"
                                  className="input-field"
                                  value={systemPromptStyle}
                                  onChange={e => setSystemPromptStyle(e.target.value)}
                                >
                                  <option value="clickbait">Clickbaity / Viral</option>
                                  <option value="educational">Educational / Informative</option>
                                  <option value="news">News / Summary</option>
                                  <option value="story">Storytelling / Creative</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>
                            )}
                            {/* System Prompt Preview or Custom */}
                            {isAIToggle && systemPromptStyle !== "custom" && (
                              <div className="input-group" style={{ marginBottom: "1rem" }}>
                                <label className="input-label">System Prompt Preview</label>
                                <div className="input-field" style={{ background: "rgba(255,255,255,0.03)", color: "#9ca3af", fontSize: "0.9rem" }}>
                                  {systemPromptStyle === "clickbait" && "Write a viral, clickbaity YouTube Shorts script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }"}
                                  {systemPromptStyle === "educational" && "Write an educational, informative script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }"}
                                  {systemPromptStyle === "news" && "Write a concise news summary script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }"}
                                  {systemPromptStyle === "story" && "Write a creative, storytelling script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }"}
                                </div>
                              </div>
                            )}
                            {isAIToggle && systemPromptStyle === "custom" && (
                              <div className="input-group" style={{ marginBottom: "1rem" }}>
                                <label className="input-label" htmlFor="custom-system-prompt">
                                  Custom System Prompt
                                </label>
                                <motion.textarea
                                  id="custom-system-prompt"
                                  className="input-field"
                                  value={customSystemPrompt}
                                  onChange={e => setCustomSystemPrompt(e.target.value)}
                                  placeholder='Enter your own system prompt. E.g., "Write a script for the given topic. Respond ONLY with a JSON object: { \"script_text\": \"...\" }"'
                                  rows={3}
                                  whileFocus={{ scale: 1.01 }}
                                />
                              </div>
                            )}
                            
                            {/* Game Selection Dropdown */}
                            <div className="input-group" style={{ marginBottom: "1rem" }}>
                              <label className="input-label">Select Game</label>
                              <CustomDropdown
                                options={gameOptions}
                                selectedValue={selectedGame}
                                onChange={setSelectedGame}
                                label="Select Game"
                              />
                            </div>

                            {/* Aspect Ratio Dropdown */}
                            <div className="input-group" style={{ marginBottom: "1rem" }}>
                              <label className="input-label">Aspect Ratio</label>
                              <CustomDropdown
                                options={aspectRatioOptions}
                                selectedValue={aspectRatio}
                                onChange={setAspectRatio}
                                label="Select Aspect Ratio"
                              />
                            </div>
                            
                            {/* Input Field */}
                            <div className="input-group">
                              <label className="input-label">
                                {isAIToggle ? "What's your topic?" : "Your script"}
                              </label>
                              <div>
                                <motion.textarea
                                  className="input-field"
                                  value={isAIToggle ? topic : userScript}
                                  onChange={isAIToggle ? handleTopicChange : handleScriptChange}
                                  placeholder={isAIToggle ? "e.g., The psychology behind viral content..." : "Write your complete script here..."}
                                  rows={isAIToggle ? 3 : 6}
                                  whileFocus={{ scale: 1.01 }}
                                  style={{ width: "100%", boxSizing: "border-box" }}
                                  maxLength={isAIToggle ? 500 : undefined}
                                />
                                {/* Word count for user script */}
                                {!isAIToggle && (
                                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px", marginBottom: "-8px" }}>
                                    <span
                                      style={{
                                        fontSize: "0.95rem",
                                        fontWeight: 600,
                                        color:
                                          userScript.length > MAX_CHARS
                                            ? "#FF6B6B"
                                            : "#7CFFCB",
                                        background: "none",
                                        padding: 0,
                                        borderRadius: 0,
                                        pointerEvents: "none",
                                        userSelect: "none"
                                      }}
                                    >
                                      {userScript.length} / {MAX_CHARS}
                                    </span>
                                  </div>
                                )}
                                {/* Word count for AI topic input */}
                                {isAIToggle && (
                                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px", marginBottom: "-8px" }}>
                                    <span
                                      style={{
                                        fontSize: "0.95rem",
                                        fontWeight: 600,
                                        color:
                                          topic.length > 500
                                            ? "#FF6B6B"
                                            : "#7CFFCB",
                                        background: "none",
                                        padding: 0,
                                        borderRadius: 0,
                                        pointerEvents: "none",
                                        userSelect: "none"
                                      }}
                                    >
                                      {topic.length} / 500
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Terms of Service Checkbox */}
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
                              <input
                                type="checkbox"
                                id="terms-agree"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                style={{ marginRight: '0.5rem' }}
                              />
                              <label htmlFor="terms-agree" style={{ fontSize: '0.9rem', color: '#ccc' }}>
                                I agree to the <span className="footer-link" onClick={() => setCurrentPage('terms')}>Terms of Service</span>
                              </label>
                            </div>

                            {/* Generate Button */}
                            <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-end", marginTop: "2px" }}>
                              <motion.button
                                className="generate-button"
                                onClick={handleGenerateVideo}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                disabled={
                                  isLoading ||
                                  (!isAIToggle &&
                                    (userScript.trim().length === 0 ||
                                      userScript.length > MAX_CHARS)) ||
                                  (isAIToggle && topic.length > 500) ||
                                  !agreedToTerms
                                }
                                style={{ marginTop: "8px" }}
                              >
                                <Play className="button-icon" />
                                {isAIToggle ? "Generate Script" : "Create Video"}
                              </motion.button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      /* Script Review */
                      <div className="script-review">
                        <h3>Review Your Script</h3>
                        <div>
                          <motion.textarea
                            className="input-field"
                            value={generatedAIScript}
                            onChange={(e) => setGeneratedAIScript(e.target.value)}
                            rows={8}
                            whileFocus={{ scale: 1.01 }}
                            style={{ width: "100%", boxSizing: "border-box" }}
                          />
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px", marginBottom: "-8px" }}>
                            <span
                              style={{
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                color:
                                  generatedAIScript.length > MAX_CHARS
                                    ? "#FF6B6B"
                                    : "#7CFFCB",
                                background: "none",
                                padding: 0,
                                borderRadius: 0,
                                pointerEvents: "none",
                                userSelect: "none"
                              }}
                            >
                              {generatedAIScript.length} / {MAX_CHARS}
                            </span>
                          </div>
                        </div>
                        {/* If the AI output is empty or looks like JSON, show a warning */}
                        {(!generatedAIScript || generatedAIScript.trim().startsWith("{")) && (
                          <div style={{ color: "#ef4444", marginTop: "1rem", textAlign: "center" }}>
                            Error: AI output was not valid. Please try again or check your prompt.
                          </div>
                        )}
                        <div className="button-group">
                          <motion.button
                            className="secondary-button"
                            onClick={() => setScriptReviewMode(false)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            Back
                          </motion.button>
                          <motion.button
                            className="generate-button"
                            onClick={handleProceedWithAIScript}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={
                              generatedAIScript.trim().length === 0 ||
                              generatedAIScript.length > MAX_CHARS
                            }
                          >
                            <Play className="button-icon" />
                            Create Video
                          </motion.button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        ) : (
          /* Video Result */
          <motion.div 
            className={`result-section ${aspectRatio}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <VideoDisplay videoUrl={videoUrl} />
            <motion.button
              className="back-button"
              onClick={handleGoHome}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Create Another
            </motion.button>
          </motion.div>
        )}
      </main>

      {/* Footer - only show on home page */}
      {currentPage === 'home' && <Footer currentPage={currentPage} setCurrentPage={setCurrentPage} />}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
};

export default App;
